import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Chevron navigation and scroll progress for the content panels section.
 *
 * The panels themselves are laid out entirely in CSS: a grid on desktop, a
 * scroll-snap track on mobile. This component only measures that track. When
 * it does not overflow — the desktop grid — the host loses its
 * `data-overflowing` attribute and the CSS hides both the chevrons and the
 * progress bar, so the controls appear only while the scrollable container is
 * in use. Without JavaScript nothing is shown and the track still scrolls and
 * snaps natively.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [progressThumb]
 *
 * @extends {Component<Refs>}
 */
class ContentPanelsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;

    track.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(track);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Scrolls the track one panel back.
   */
  previous() {
    this.#scrollByStep(-1);
  }

  /**
   * Scrolls the track one panel forward.
   */
  next() {
    this.#scrollByStep(1);
  }

  /**
   * The distance between two adjacent panels, falling back to one viewport of
   * the track when fewer than two panels are rendered.
   *
   * @returns {number}
   */
  get #step() {
    const { track } = this.refs;
    const [first, second] = track.children;

    if (first instanceof HTMLElement && second instanceof HTMLElement) {
      return second.offsetLeft - first.offsetLeft;
    }

    return track.clientWidth;
  }

  /**
   * @param {number} direction - 1 for forward, -1 for back.
   */
  #scrollByStep(direction) {
    const { track } = this.refs;
    const delta = direction * this.#step;

    if (prefersReducedMotion()) {
      track.scrollBy({ left: delta, behavior: 'instant' });
      return;
    }

    const from = track.scrollLeft;
    track.scrollBy({ left: delta, behavior: 'smooth' });

    // Some environments suppress smooth programmatic scrolling inside
    // scroll-snap containers; if nothing moved, fall back to instant. Resync
    // afterwards either way, so the controls stay correct even where the
    // scroll event is throttled away.
    setTimeout(() => {
      if (track.scrollLeft === from) {
        track.scrollBy({ left: delta, behavior: 'instant' });
      }

      this.#update();
    }, 250);
  }

  #update = () => {
    const { track, previous, next, progress, progressThumb } = this.refs;
    // A track with no layout yet — a hidden tab, a collapsed ancestor — would
    // measure as overflowing and flash the controls on. Wait for the
    // ResizeObserver to report a real box instead.
    if (!track || track.clientWidth === 0) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const overflowing = maxScroll > 1;

    this.toggleAttribute('data-overflowing', overflowing);

    // A scrollable region needs a tab stop of its own so it can be reached by
    // keyboard; the desktop grid does not scroll, so it should not take one.
    if (overflowing) {
      track.setAttribute('tabindex', '0');
    } else {
      track.removeAttribute('tabindex');
    }

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = !overflowing || track.scrollLeft <= 1;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = !overflowing || track.scrollLeft >= maxScroll - 1;
    }

    if (!overflowing || !(progress instanceof HTMLElement) || !(progressThumb instanceof HTMLElement)) return;

    // A thumb sized and positioned as a fraction of the full scroll range —
    // rather than one segment per panel — always lands flush with the track's
    // end exactly when the track itself is scrolled all the way, however many
    // panels are visible at once.
    const trackWidth = progress.clientWidth;
    progressThumb.style.setProperty('--content-panels-progress-x', `${(track.scrollLeft / track.scrollWidth) * trackWidth}px`);
    progressThumb.style.setProperty('--content-panels-progress-scale', `${track.clientWidth / track.scrollWidth}`);
  };
}

if (!customElements.get('content-panels-component')) {
  customElements.define('content-panels-component', ContentPanelsComponent);
}
